import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, Clock, Music, Disc, ChevronRight, ArrowLeft, Instagram, MapPin } from 'lucide-react';
import BookingWizard from './BookingWizard';
import { Toaster } from './ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const JIMMYS_LOGO = '/api/uploads/jimmys_logo.png';
const PESCHIERA_LOGO = "https://customer-assets.emergentagent.com/job_3a008eb2-6f58-4bf6-b16e-78d189a6aa15/artifacts/lymh6mbi_LA%20PESCHIERA%20CLUB%20WHITE%20E%20STREET.png";

function formatLongDate(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return dateStr; }
}

// ── Dynamic underground background ───────────────────────────
function JimmysAnimatedBg() {
  // Pre-compute 18 floating dots with random delays/positions
  const dots = Array.from({ length: 18 }, (_, i) => ({
    left: `${(i * 5.5 + 3) % 100}%`,
    size: 3 + (i % 4),
    color: i % 3 === 0 ? '#ec4899' : i % 3 === 1 ? '#a855f7' : '#f0abfc',
    duration: 14 + (i % 7) * 2,
    delay: -((i * 1.3) % 12),
  }));
  return (
    <div
      data-testid="jimmys-animated-bg"
      aria-hidden
      style={{
        position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0,
        overflow: 'hidden',
      }}
    >
      {/* Base dark wash */}
      <div style={{ position: 'absolute', inset: 0, background: '#05050a' }} />

      {/* Drifting magenta blob */}
      <div
        className="jimmys-bg-blob"
        style={{
          top: '-12%', left: '-8%',
          width: '52vw', height: '52vw', maxWidth: 720, maxHeight: 720,
          background: 'radial-gradient(circle, rgba(236,72,153,0.45) 0%, rgba(236,72,153,0) 70%)',
          animation: 'jimmysBlobFloat1 18s ease-in-out infinite',
        }}
      />
      {/* Drifting purple blob */}
      <div
        className="jimmys-bg-blob"
        style={{
          bottom: '-15%', right: '-10%',
          width: '58vw', height: '58vw', maxWidth: 820, maxHeight: 820,
          background: 'radial-gradient(circle, rgba(168,85,247,0.4) 0%, rgba(168,85,247,0) 70%)',
          animation: 'jimmysBlobFloat2 22s ease-in-out infinite',
        }}
      />
      {/* Center pink accent */}
      <div
        className="jimmys-bg-blob"
        style={{
          top: '30%', left: '40%',
          width: '32vw', height: '32vw', maxWidth: 460, maxHeight: 460,
          background: 'radial-gradient(circle, rgba(240,171,252,0.25) 0%, rgba(240,171,252,0) 70%)',
          animation: 'jimmysBlobFloat3 26s ease-in-out infinite',
        }}
      />

      {/* Laser sweep — magenta */}
      <div
        className="jimmys-bg-laser"
        style={{
          left: '20%', width: '90px',
          background: 'linear-gradient(90deg, transparent, rgba(236,72,153,0.55), transparent)',
          filter: 'blur(2px)',
          animation: 'jimmysLaserSweep 9s linear infinite',
        }}
      />
      {/* Laser sweep — purple, reverse, slower */}
      <div
        className="jimmys-bg-laser"
        style={{
          left: '60%', width: '60px',
          background: 'linear-gradient(90deg, transparent, rgba(168,85,247,0.55), transparent)',
          filter: 'blur(3px)',
          animation: 'jimmysLaserSweepReverse 14s linear infinite',
          animationDelay: '-4s',
        }}
      />

      {/* Pulsing grid */}
      <div
        style={{
          position: 'absolute', inset: 0,
          backgroundImage:
            'linear-gradient(rgba(236,72,153,0.18) 1px, transparent 1px), linear-gradient(90deg, rgba(168,85,247,0.18) 1px, transparent 1px)',
          backgroundSize: '70px 70px',
          maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 75%)',
          WebkitMaskImage: 'radial-gradient(ellipse at center, black 30%, transparent 75%)',
          animation: 'jimmysGridPulse 5s ease-in-out infinite',
        }}
      />

      {/* Grain */}
      <div
        style={{
          position: 'absolute', inset: 0, opacity: 0.08,
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='180' height='180'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
          mixBlendMode: 'overlay',
        }}
      />

      {/* Floating bokeh dots rising from the bottom */}
      {dots.map((d, i) => (
        <div
          key={i}
          className="jimmys-bg-dot"
          style={{
            left: d.left,
            width: `${d.size}px`,
            height: `${d.size}px`,
            background: d.color,
            boxShadow: `0 0 ${d.size * 3}px ${d.color}`,
            animation: `jimmysDotFloat ${d.duration}s linear infinite`,
            animationDelay: `${d.delay}s`,
          }}
        />
      ))}
    </div>
  );
}

export default function JimmysPage() {
  const [events, setEvents] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [listCount, setListCount] = useState(null);

  useEffect(() => {
    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname);
    }
    axios.get(`${API}/events?club=jimmys`)
      .then(r => { setEvents(r.data); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);

  // Featured event = nearest upcoming Jimmy's event
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = events.filter(e => e.date >= today).sort((a, b) => a.date.localeCompare(b.date));
  const featured = upcoming[0] || events[0] || null;

  // Poll list count every 25s if enabled
  useEffect(() => {
    if (!featured?.id || !featured?.show_list_counter) {
      setListCount(null);
      return;
    }
    const fetchCount = () => {
      axios.get(`${API}/events/${featured.id}/list-count`)
        .then(r => r.data.enabled && setListCount(r.data))
        .catch(() => {});
    };
    fetchCount();
    const t = setInterval(fetchCount, 25000);
    return () => clearInterval(t);
  }, [featured?.id, featured?.show_list_counter]);

  const scrollToBooking = () => {
    document.getElementById('prenota')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div data-testid="jimmys-page" style={{ background: '#05050a', minHeight: '100vh', color: '#fff', position: 'relative', overflow: 'hidden' }}>
      <JimmysAnimatedBg />
      <div style={{ position: 'relative', zIndex: 1 }}>
      <Toaster position="top-right" />

      {/* Top bar — minimal underground vibe */}
      <header
        data-testid="jimmys-topbar"
        style={{
          position: 'sticky', top: 0, zIndex: 50,
          backdropFilter: 'blur(16px)',
          background: 'rgba(5,5,10,0.75)',
          borderBottom: '1px solid rgba(236,72,153,0.15)',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-16">
          <a
            href="/"
            data-testid="jimmys-back-link"
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors font-manrope font-600 text-sm"
          >
            <ArrowLeft size={18} /> <span className="hidden sm:inline">La Peschiera</span>
          </a>
          <img
            src={JIMMYS_LOGO}
            alt="Jimmy's Club"
            className="h-9 md:h-11 object-contain"
            data-testid="jimmys-topbar-logo"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <a
            href="https://www.instagram.com/jimmysclubvaldengo"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="jimmys-ig-link"
            className="text-gray-400 hover:text-pink-400 transition-colors"
          >
            <Instagram size={20} />
          </a>
        </div>
      </header>

      {/* HERO Section */}
      <section
        data-testid="jimmys-hero"
        className="relative overflow-hidden"
        style={{
          minHeight: '92vh',
          background: 'transparent',
        }}
      >
        {/* Grain / noise overlay */}
        <div
          aria-hidden
          style={{
            position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.06,
            backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='180' height='180'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
          }}
        />

        {/* Subtle scan lines */}
        <div
          aria-hidden
          style={{
            position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.04,
            background: 'repeating-linear-gradient(0deg, transparent 0 2px, rgba(255,255,255,0.5) 2px 3px)',
          }}
        />

        <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-16 md:pt-24 pb-20">
          {/* Logo big */}
          <div className="flex flex-col items-center mb-10">
            <img
              src={JIMMYS_LOGO}
              alt="Jimmy's Club"
              data-testid="jimmys-hero-logo"
              className="object-contain drop-shadow-2xl"
              style={{
                maxHeight: '180px',
                maxWidth: '90%',
                filter: 'drop-shadow(0 0 22px rgba(236,72,153,0.55)) drop-shadow(0 0 8px rgba(168,85,247,0.4))',
              }}
              onError={(e) => {
                e.target.outerHTML = '<h1 style="font-family:Playfair Display,serif;font-size:5rem;font-weight:900;color:#fff;letter-spacing:0.08em;text-shadow:0 0 30px rgba(236,72,153,0.6)">JIMMY\'S</h1>';
              }}
            />
            <p
              className="text-center mt-4 font-manrope font-600 uppercase tracking-[0.45em] text-xs md:text-sm"
              style={{ color: '#ec4899' }}
              data-testid="jimmys-tagline"
            >
              The Club Within The Club
            </p>
          </div>

          {!loaded ? (
            <div className="flex justify-center py-20">
              <div className="w-12 h-12 rounded-full border-2 border-pink-500/30 border-t-pink-500 animate-spin" />
            </div>
          ) : !featured ? (
            <div className="text-center py-16" data-testid="jimmys-no-events">
              <p className="font-playfair text-3xl text-white mb-3">Nessuna serata in programma</p>
              <p className="text-gray-400 font-manrope">Torna presto per il prossimo evento.</p>
            </div>
          ) : (
            /* Featured Event — single big card */
            <div
              data-testid="jimmys-featured-event"
              className="max-w-5xl mx-auto"
            >
              <div
                className="relative rounded-3xl overflow-hidden grid md:grid-cols-2 gap-0"
                style={{
                  background: 'linear-gradient(135deg, rgba(20,10,30,0.85), rgba(10,5,20,0.95))',
                  border: '1px solid rgba(236,72,153,0.3)',
                  boxShadow: '0 20px 80px rgba(236,72,153,0.18), 0 0 1px rgba(236,72,153,0.5) inset',
                }}
              >
                {/* Event image */}
                <div className="relative aspect-square md:aspect-auto md:min-h-[480px]" style={{ background: '#0a0a14' }}>
                  <img
                    src={featured.image_url || '/api/uploads/jimmys_event_flyer.png'}
                    alt={featured.title}
                    data-testid="jimmys-event-image"
                    className="w-full h-full object-cover"
                    style={{ filter: 'saturate(1.1) contrast(1.05)' }}
                    onError={(e) => { e.target.src = '/api/uploads/jimmys_event_flyer.png'; }}
                  />
                  <div
                    aria-hidden
                    className="absolute inset-0"
                    style={{ background: 'linear-gradient(180deg, transparent 50%, rgba(5,5,10,0.6) 100%)' }}
                  />
                  {featured.sold_out && (
                    <div
                      className="absolute top-5 left-5 px-4 py-2 rounded-full font-manrope font-800 uppercase tracking-widest text-xs"
                      data-testid="jimmys-sold-out-badge"
                      style={{ background: '#ef4444', color: '#fff' }}
                    >
                      SOLD OUT
                    </div>
                  )}
                </div>

                {/* Event info */}
                <div className="p-7 md:p-10 flex flex-col justify-center">
                  <p
                    className="section-eyebrow mb-3"
                    style={{ color: '#ec4899', letterSpacing: '0.35em' }}
                  >
                    {upcoming.length > 0 ? '◆ PROSSIMA SERATA' : '◆ ULTIMA SERATA'}
                  </p>
                  <h2
                    className="font-playfair font-900 text-white leading-[1.05] mb-5"
                    data-testid="jimmys-event-title"
                    style={{
                      fontSize: 'clamp(2rem, 4.6vw, 3.5rem)',
                      textShadow: '0 0 30px rgba(236,72,153,0.25)',
                    }}
                  >
                    {featured.title}
                  </h2>

                  <div className="space-y-3 mb-7">
                    <div className="flex items-center gap-3 text-gray-200">
                      <Calendar size={18} className="text-pink-400 shrink-0" />
                      <span className="font-manrope font-600 text-base md:text-lg capitalize">
                        {formatLongDate(featured.date)}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-200">
                      <Clock size={18} className="text-pink-400 shrink-0" />
                      <span className="font-manrope font-600 text-base md:text-lg">
                        Ore {featured.time}
                      </span>
                    </div>
                    {featured.dj && (
                      <div className="flex items-center gap-3 text-gray-200">
                        <Disc size={18} className="text-pink-400 shrink-0" />
                        <span className="font-manrope font-600 text-base md:text-lg">
                          {featured.dj}
                        </span>
                      </div>
                    )}
                    {featured.music_genre && (
                      <div className="flex items-center gap-3 text-gray-200">
                        <Music size={18} className="text-pink-400 shrink-0" />
                        <span className="font-manrope text-sm md:text-base text-gray-300">
                          {featured.music_genre}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center gap-3 text-gray-200">
                      <MapPin size={18} className="text-pink-400 shrink-0" />
                      <span className="font-manrope text-sm md:text-base text-gray-300">
                        Inside La Peschiera Club — Valdengo (BI)
                      </span>
                    </div>
                  </div>

                  {/* Age & Dress code chips */}
                  {(featured.age_restriction || featured.dress_code) && (
                    <div className="flex flex-wrap gap-2 mb-5" data-testid="jimmys-policy-chips">
                      {featured.age_restriction && (
                        <span
                          data-testid="jimmys-age-chip"
                          className="px-3 py-1.5 rounded-full font-manrope font-700 text-xs uppercase tracking-wider"
                          style={{
                            background: 'rgba(236,72,153,0.12)',
                            border: '1px solid rgba(236,72,153,0.35)',
                            color: '#f9a8d4',
                          }}
                        >
                          {featured.age_restriction}
                        </span>
                      )}
                      {featured.dress_code && (
                        <span
                          data-testid="jimmys-dress-chip"
                          className="px-3 py-1.5 rounded-full font-manrope font-700 text-xs uppercase tracking-wider"
                          style={{
                            background: 'rgba(168,85,247,0.12)',
                            border: '1px solid rgba(168,85,247,0.35)',
                            color: '#d8b4fe',
                          }}
                        >
                          👔 {featured.dress_code}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Live list counter */}
                  {featured.show_list_counter && listCount && listCount.enabled && listCount.people > 0 && (
                    <div
                      data-testid="jimmys-list-counter"
                      className="flex items-center gap-3 mb-5 px-4 py-3 rounded-2xl"
                      style={{
                        background: 'linear-gradient(135deg, rgba(236,72,153,0.16), rgba(168,85,247,0.12))',
                        border: '1px solid rgba(236,72,153,0.4)',
                        boxShadow: '0 0 24px rgba(236,72,153,0.18)',
                      }}
                    >
                      <span style={{ fontSize: '1.5rem' }} className="animate-pulse">🔥</span>
                      <div className="flex-1">
                        <p className="font-playfair font-bold text-white text-lg leading-tight">
                          {listCount.people} {listCount.people === 1 ? 'persona' : 'persone'} già in lista
                        </p>
                        <p className="font-manrope text-xs text-gray-400">
                          Aggiornato in tempo reale
                        </p>
                      </div>
                    </div>
                  )}

                  {featured.description && (
                    <p className="text-gray-400 font-manrope text-sm md:text-base mb-7 leading-relaxed">
                      {featured.description}
                    </p>
                  )}

                  {/* CTA "Entra in lista" — hidden if bookings_disabled or sold_out */}
                  {featured.bookings_disabled ? (
                    <div
                      data-testid="jimmys-view-only-badge"
                      className="inline-flex items-center gap-2 self-start px-5 py-3 rounded-full font-manrope font-700 uppercase tracking-[0.2em] text-xs"
                      style={{
                        background: 'rgba(168,85,247,0.12)',
                        border: '1px solid rgba(168,85,247,0.4)',
                        color: '#c084fc',
                      }}
                    >
                      <span style={{ fontSize: '1rem' }}>👁</span>
                      Solo annuncio — prenotazioni non disponibili
                    </div>
                  ) : (
                    <button
                      onClick={scrollToBooking}
                      disabled={featured.sold_out}
                      data-testid="jimmys-entra-in-lista-btn"
                      className="group relative inline-flex items-center justify-center gap-3 font-manrope font-800 uppercase tracking-[0.18em] text-base md:text-lg transition-all duration-300 rounded-full overflow-hidden"
                      style={{
                        padding: '1.1rem 2.4rem',
                        background: featured.sold_out
                          ? 'rgba(239,68,68,0.15)'
                          : 'linear-gradient(135deg, #ec4899, #a855f7)',
                        color: '#fff',
                        border: '1px solid rgba(255,255,255,0.15)',
                        boxShadow: featured.sold_out
                          ? 'none'
                          : '0 10px 40px rgba(236,72,153,0.45), 0 0 0 1px rgba(236,72,153,0.6) inset',
                        cursor: featured.sold_out ? 'not-allowed' : 'pointer',
                        opacity: featured.sold_out ? 0.5 : 1,
                      }}
                      onMouseEnter={(e) => {
                        if (!featured.sold_out) {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 16px 50px rgba(236,72,153,0.6), 0 0 0 1px rgba(236,72,153,0.8) inset';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!featured.sold_out) {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 10px 40px rgba(236,72,153,0.45), 0 0 0 1px rgba(236,72,153,0.6) inset';
                        }
                      }}
                    >
                      {featured.sold_out ? 'SOLD OUT' : 'ENTRA IN LISTA'}
                      {!featured.sold_out && <ChevronRight size={22} className="transition-transform group-hover:translate-x-1" />}
                    </button>
                  )}

                  {!featured.bookings_disabled && (
                    <p className="text-gray-500 font-manrope text-xs mt-4">
                      Pagamento direttamente all'ingresso. Prenotazione gratuita.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Booking Wizard — reused, hidden when bookings_disabled or sold_out */}
      {featured && !featured.sold_out && !featured.bookings_disabled && (
        <div
          data-testid="jimmys-booking-section"
          style={{
            background: 'linear-gradient(180deg, rgba(5,5,10,0.4) 0%, rgba(10,10,20,0.6) 100%)',
            position: 'relative',
          }}
        >
          <BookingWizard
            events={[featured]}
            clubVariant="jimmys"
            headerEyebrow="ENTRA IN LISTA"
            headerTitle="Riserva il tuo posto"
            headerSubtitle="Compila il modulo per entrare in lista. Pagamento solo in cassa all'ingresso."
          />
        </div>
      )}

      {/* Footer minimal */}
      <footer
        data-testid="jimmys-footer"
        style={{ background: '#02020a', borderTop: '1px solid rgba(236,72,153,0.12)', padding: '40px 16px' }}
      >
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-5 text-center md:text-left">
          <div className="flex items-center gap-4">
            <img src={JIMMYS_LOGO} alt="Jimmy's Club" className="h-9 object-contain" onError={e => e.target.style.display = 'none'} />
            <div className="hidden md:block w-px h-8" style={{ background: 'rgba(236,72,153,0.2)' }} />
            <img src={PESCHIERA_LOGO} alt="La Peschiera Club" className="h-7 object-contain opacity-80" />
          </div>
          <p className="text-gray-500 font-manrope text-xs">
            Jimmy's Club &copy; {new Date().getFullYear()} — Inside La Peschiera Club, Valdengo (BI)
          </p>
        </div>
      </footer>
      </div>
    </div>
  );
}
